import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Provider, useSelector } from 'react-redux';
import { Toaster } from 'react-hot-toast';
import store from './store';
import { useAppSettings } from './hooks/useAppSettings';
import 'leaflet/dist/leaflet.css';

// ── Auth ─────────────────────────────────────────────────────
import LoginPage        from './pages/auth/LoginPage';
import CitizenLoginPage from './pages/auth/CitizenLoginPage';
import RegisterPage     from './pages/auth/RegisterPage';
import ForgotPassword    from './pages/auth/ForgotPassword';
import PublicDownload   from './pages/auth/PublicDownload';
import ChangePassword   from './pages/auth/ChangePassword';

// ── Citizen ──────────────────────────────────────────────────
import CitizenLayout        from './layouts/CitizenLayout';
import CitizenDashboard     from './pages/citizen/Dashboard';
import ApplyNOC             from './pages/citizen/ApplyNOC';
import MyApplications       from './pages/citizen/MyApplications';
import ApplicationStatus    from './pages/citizen/ApplicationStatus';
import CitizenNotifications from './pages/citizen/Notifications';
import DownloadNOC          from './pages/citizen/DownloadNOC';
import CitizenProfile       from './pages/citizen/Profile';
import CitizenFeedback      from './pages/citizen/Feedback';

// ── Department ───────────────────────────────────────────────
import DeptLayout        from './layouts/DeptLayout';
import DeptDashboard     from './pages/department/Dashboard';
import DeptApplications  from './pages/department/Applications';
import ApplicationDetail from './pages/department/ApplicationDetail';
import DeptNotifications from './pages/department/Notifications';
import DeptReports       from './pages/department/Reports';
import DeptPendingAlerts from './pages/department/PendingAlerts';

// ── Admin ────────────────────────────────────────────────────
import AdminLayout           from './layouts/AdminLayout';
import AdminDashboard        from './pages/admin/Dashboard';
import AdminApplications     from './pages/admin/Applications';
import AdminReports          from './pages/admin/Reports';
import NocReport             from './pages/admin/NocReport';
import DeptSummaryReport    from './pages/admin/DeptSummaryReport';
import DeptNocReport        from './pages/admin/DeptNocReport';
import AdminFeedbacks        from './pages/admin/Feedbacks';
import PendingReport         from './pages/admin/PendingReport';
import ActivityLogs          from './pages/admin/ActivityLogs';
import FlowTracking          from './pages/admin/FlowTracking';
import WorkflowRoutingMaster from './pages/admin/WorkflowRoutingMaster';
import SaraiZoneRouting      from './pages/admin/SaraiZoneRouting';
import OperatorManager       from './pages/admin/OperatorManager';
import NocAccessConfig       from './pages/admin/NocAccessConfig';
import NocTypeMaster         from './pages/admin/masters/NocTypeMaster';
import DepartmentMaster      from './pages/admin/masters/DepartmentMaster';
import DesignationMaster     from './pages/admin/masters/DesignationMaster';
import EmployeeMaster        from './pages/admin/masters/EmployeeMaster';
import UserMaster            from './pages/admin/masters/UserMaster';
import DocumentDemandMaster  from './pages/admin/masters/DocumentDemandMaster';
import NoticeMaster          from './pages/admin/masters/NoticeMaster';
import RbacRoles             from './pages/admin/rbac/Roles';
import RbacPermissions       from './pages/admin/rbac/Permissions';

// ── Developer ────────────────────────────────────────────────
import DevLayout      from './layouts/DevLayout';
import DevDashboard   from './pages/developer/Dashboard';
import ThemeSettings  from './pages/developer/ThemeSettings';
import SystemSettings from './pages/developer/SystemSettings';
import MenuMaster     from './pages/developer/MenuMaster';

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { token, role } = useSelector(s => s.auth);
  if (!token) return <Navigate to="/login" replace />;
  if (allowedRoles && !allowedRoles.includes(role)) return <Navigate to="/unauthorized" replace />;
  return children;
};

const RoleRedirect = () => {
  const { role } = useSelector(s => s.auth);
  if (role === 'citizen')                               return <Navigate to="/user/dashboard" replace />;
  if (role === 'related_dept' || role === 'final_dept') return <Navigate to="/dept/dashboard" replace />;
  if (role === 'admin')                                 return <Navigate to="/admin/dashboard" replace />;
  if (role === 'developer')                             return <Navigate to="/developer/dashboard" replace />;
  return <Navigate to="/citizen-login" replace />;
};

function AppRoutes() {
  useAppSettings();
  return (
    <Routes>
      {/* Public */}
      <Route path="/"               element={<RoleRedirect />} />
      <Route path="/login"          element={<LoginPage />} />
      <Route path="/citizen-login"  element={<CitizenLoginPage />} />
      <Route path="/register"       element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/download-noc"     element={<PublicDownload />} />
      <Route path="/unauthorized"   element={<div className="flex items-center justify-center h-screen text-2xl text-red-500">Access Denied</div>} />

      {/* Citizen */}
      <Route path="/user" element={<ProtectedRoute allowedRoles={['citizen']}><CitizenLayout /></ProtectedRoute>}>
        <Route path="dashboard"        element={<CitizenDashboard />} />
        <Route path="apply"            element={<ApplyNOC />} />
        <Route path="applications"     element={<MyApplications />} />
        <Route path="applications/:id" element={<ApplicationStatus />} />
        <Route path="notifications"    element={<CitizenNotifications />} />
        <Route path="download"         element={<DownloadNOC />} />
        <Route path="profile"          element={<CitizenProfile />} />
        <Route path="feedback"         element={<CitizenFeedback />} />
      </Route>

      {/* Department */}
      <Route path="/dept" element={<ProtectedRoute allowedRoles={['related_dept','final_dept']}><DeptLayout /></ProtectedRoute>}>
        <Route path="dashboard"        element={<DeptDashboard />} />
        <Route path="applications"     element={<DeptApplications />} />
        <Route path="applications/:id" element={<ApplicationDetail />} />
        <Route path="notifications"    element={<DeptNotifications />} />
        <Route path="reports"          element={<DeptReports />} />
        <Route path="change-password"  element={<ChangePassword />} />
        <Route path="pending-alerts"  element={<DeptPendingAlerts />} />
      </Route>

      {/* Admin */}
      <Route path="/admin" element={<ProtectedRoute allowedRoles={['admin','developer']}><AdminLayout /></ProtectedRoute>}>
        <Route path="dashboard"               element={<AdminDashboard />} />
        <Route path="applications"            element={<AdminApplications />} />
        <Route path="applications/:id"       element={<ApplicationDetail />} />
        <Route path="masters/noc"             element={<NocTypeMaster />} />
        <Route path="masters/department"      element={<DepartmentMaster />} />
        <Route path="masters/designation"     element={<DesignationMaster />} />
        <Route path="masters/employee"        element={<EmployeeMaster />} />
        <Route path="masters/users"           element={<UserMaster />} />
        <Route path="masters/document-demand" element={<DocumentDemandMaster />} />
        <Route path="masters/notice"          element={<NoticeMaster />} />
        <Route path="rbac/roles"              element={<RbacRoles />} />
        <Route path="rbac/menu-permissions"   element={<RbacPermissions />} />
        <Route path="operator-manager"        element={<OperatorManager />} />
        <Route path="noc-access-config"       element={<NocAccessConfig />} />
        <Route path="workflow-routing"        element={<WorkflowRoutingMaster />} />
        <Route path="sarai-zone-routing"      element={<SaraiZoneRouting />} />
        <Route path="flow-tracking"           element={<FlowTracking />} />
        <Route path="reports"                 element={<AdminReports />} />
        <Route path="noc-report"              element={<NocReport />} />
        <Route path="dept-report"             element={<DeptSummaryReport />} />
        <Route path="dept-noc-report"         element={<DeptNocReport />} />
        <Route path="feedbacks"               element={<AdminFeedbacks />} />
        <Route path="pending-report"          element={<PendingReport />} />
        <Route path="activity-logs"           element={<ActivityLogs />} />
        <Route path="change-password"         element={<ChangePassword />} />
      </Route>

      {/* Developer */}
      <Route path="/developer" element={<ProtectedRoute allowedRoles={['developer']}><DevLayout /></ProtectedRoute>}>
        <Route path="dashboard"               element={<DevDashboard />} />
        <Route path="theme"                   element={<ThemeSettings />} />
        <Route path="system"                  element={<SystemSettings />} />
        <Route path="rbac/menus"              element={<MenuMaster />} />
        <Route path="rbac/roles"              element={<RbacRoles />} />
        <Route path="rbac/menu-permissions"   element={<RbacPermissions />} />
        <Route path="operator-manager"        element={<OperatorManager />} />
        <Route path="noc-access-config"       element={<NocAccessConfig />} />
        <Route path="masters/noc"             element={<NocTypeMaster />} />
        <Route path="masters/department"      element={<DepartmentMaster />} />
        <Route path="masters/designation"     element={<DesignationMaster />} />
        <Route path="masters/employee"        element={<EmployeeMaster />} />
        <Route path="masters/document-demand" element={<DocumentDemandMaster />} />
        <Route path="masters/notice"          element={<NoticeMaster />} />
        <Route path="masters/users"           element={<UserMaster />} />
        <Route path="workflow-routing"        element={<WorkflowRoutingMaster />} />
        <Route path="sarai-zone-routing"      element={<SaraiZoneRouting />} />
        <Route path="flow-tracking"           element={<FlowTracking />} />
        <Route path="reports"                 element={<AdminReports />} />
        <Route path="noc-report"              element={<NocReport />} />
        <Route path="dept-report"             element={<DeptSummaryReport />} />
        <Route path="dept-noc-report"         element={<DeptNocReport />} />
        <Route path="feedbacks"               element={<AdminFeedbacks />} />
        <Route path="pending-report"          element={<PendingReport />} />
        <Route path="activity-logs"           element={<ActivityLogs />} />
        <Route path="applications"            element={<AdminApplications />} />
        <Route path="applications/:id"       element={<ApplicationDetail />} />
        <Route path="change-password"         element={<ChangePassword />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <Provider store={store}>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" toastOptions={{ duration: 3000 }} />
      </BrowserRouter>
    </Provider>
  );
}

export default App;